import { Timestamp } from 'firebase/firestore';

import { filterPropertyTypes } from 'boot/globals';
import { uid } from 'quasar';

import { DATE_FILTER_OPERATORS } from 'src/constants/filters';

/**
 * The shape of a single filter statement, and the rules for when one is complete enough to apply.
 *
 * Moved out of the dialog rather than rewritten. This decides both what the user sees on a chip
 * and what actually filters their data, so it is the last thing that should be reworked while the
 * surface around it changes.
 */

/*
 * The two presets that need the user to pick actual dates before the filter means anything. Named
 * here rather than at each comparison because the value is persisted onto the statement: a typo in
 * one of the four places it is read disables the calendar branch with nothing to catch it.
 */
export const CUSTOM_DATES = 'custom-dates';
export const CUSTOM_RANGE = 'custom-range';

const CUSTOM_DATE_PRESETS = [CUSTOM_DATES, CUSTOM_RANGE];

/** Operator that takes two values instead of one. */
export const BETWEEN = 'between';

/**
 * An empty statement. Every field is present from the start so a statement never changes shape as
 * the user moves between editors, which is what lets one draft survive switching criteria.
 *
 * @returns {object}
 */
export function createFilterStatement() {
  return {
    fromDate: null,
    toDate: null,
    filterDateItems: [],
    filterCriteriaType: null,
    filteredItems: null,
    filterNegative: false,
    dateOperator: null,
    label: null,
  };
}

/*
 * Mirrors lodash isEmpty for the shapes filteredItems actually takes here, which are an array of
 * selected values or a string. Written out rather than reaching for isEmpty because the naive
 * !value.length it invites treats null and 0 differently, and because a number is never the value
 * in this branch: the numeric types are handled before it.
 */
function hasSelection(value) {
  if (value === null || value === undefined) {
    return false;
  }
  return Array.isArray(value) ? value.length > 0 : String(value).length > 0;
}

function isCompleteRange(range, isValid) {
  const from = range?.from;
  const to = range?.to;

  return isValid(from) && isValid(to) && from >= 0 && to >= 0 && to > from;
}

const isNonNegativeInteger = (value) => Number.isInteger(value) && value >= 0;
// Zero weeks ago is not a window the filter can express, so the count starts at one.
const isPositiveInteger = (value) => Number.isInteger(value) && value >= 1;
// Finite, not merely a number: Infinity survives an isNaN check and would apply as a bound.
const isNonNegativeNumber = (value) => Number.isFinite(value) && value >= 0;

/*
 * Ordered as well as present. A reversed range is the same failure the Timestamp check below
 * guards against: it matches nothing while still showing as an applied filter. The numeric ranges
 * are held to the same rule in isCompleteRange.
 */
const isOrderedTimestampRange = (from, to) => from instanceof Timestamp
  && to instanceof Timestamp
  && from.toMillis() <= to.toMillis();

/**
 * Whether a statement carries enough for its criterion to be applied.
 *
 * @param {{ filterType: string }} criterion
 * @param {object} statement
 * @returns {boolean}
 */
export function canApplyFilter(criterion, statement) {
  if (!criterion || !statement) {
    return false;
  }

  const { filterCriteriaType, filteredItems } = statement;

  switch (criterion.filterType) {
    case filterPropertyTypes.Date: {
      if (statement.dateOperator === DATE_FILTER_OPERATORS.IS_MORE_THAN) {
        return isPositiveInteger(filteredItems);
      }

      if (CUSTOM_DATE_PRESETS.includes(filterCriteriaType)) {
        /*
         * Timestamps rather than merely present. The engine compares these with toMillis(), so
         * anything else, a bare string among them, reads as undefined on both sides and the filter
         * silently matches nothing while still showing as applied.
         */
        return isOrderedTimestampRange(statement.fromDate, statement.toDate);
      }
      return Boolean(filterCriteriaType);
    }

    case filterPropertyTypes.StringMatch:
      return String(filteredItems ?? '').trim().length > 0;

    case filterPropertyTypes.Number:
      return filterCriteriaType === BETWEEN
        ? isCompleteRange(filteredItems, Number.isInteger)
        : isNonNegativeInteger(filteredItems);

    case filterPropertyTypes.Duration:
      return filterCriteriaType === BETWEEN
        ? isCompleteRange(filteredItems, Number.isFinite)
        : isNonNegativeNumber(filteredItems);

    // null is a real choice here, meaning "not set", so it is applyable. Only undefined is not.
    case filterPropertyTypes.Boolean:
      return typeof filteredItems === 'boolean' || filteredItems === null;

    default:
      return hasSelection(filteredItems);
  }
}

/**
 * Build the filter a criterion and its statements produce, in the shape callers store and
 * FiltersMixin reads.
 *
 * The criterion's own fields are copied onto the filter rather than referenced, because the filter
 * is persisted to localStorage and has to survive without the criteria list that produced it.
 *
 * A field a criterion does not define is null rather than absent, because a saved table view goes
 * to Firestore, which rejects an undefined value.
 *
 * @param {object} criterion
 * @param {object[]} statements
 * @returns {{ filterKey: string, filter: object }}
 */
export function buildFilter(criterion, statements) {
  return {
    filterKey: criterion.value,
    filter: {
      id: uid(),
      filterProperty: criterion.filterProperty ?? null,
      filterObject: criterion.filterObject ?? null,
      filterObjectKey: criterion.filterObjectKey ?? null,
      filterObjectForeignKey: criterion.filterObjectForeignKey ?? null,
      filterPropertyCallback: criterion.filterPropertyCallback ?? null,
      filterPropertyType: criterion.filterType ?? null,
      arrayProperty: criterion.options?.arrayProperty || null,
      filterDependency: criterion.options?.filterDependency || null,
      filterStatements: statements,
    },
  };
}
